/**
 * Azure Functions Health Dashboard
 * Muestra el estado de las funciones vía Application Insights.
 * Requiere: admin-app-insights-query desplegada + APP_INSIGHTS_APP_ID y APP_INSIGHTS_API_KEY.
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';
import { cn } from '@/lib/utils';
import { Search, RefreshCw, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AppInsightsResult {
    columns: { name: string; type: string }[];
    results: Record<string, unknown>[];
    count: number;
    query: string;
    timespan: string;
}

export interface FunctionHealthRow {
    name: string;
    total: number;
    failed: number;
    failure_rate: number;
    avg_duration: number;
    p95_duration: number;
    exceptions: number;
}

const FUNCTIONS_TIMESPANS = [
    { value: '1h', label: '1 hora' },
    { value: '6h', label: '6 horas' },
    { value: '12h', label: '12 horas' },
    { value: '1d', label: '1 día' },
    { value: '3d', label: '3 días' },
    { value: '7d', label: '7 días' },
];

function getHealthStatus(row: FunctionHealthRow): 'healthy' | 'degraded' | 'critical' {
    const hasFailures = row.failed > 0 || row.failure_rate > 0;
    const hasExceptions = row.exceptions > 0;
    if (row.failure_rate >= 20 || row.exceptions >= 10) return 'critical';
    if (row.failure_rate >= 5 || row.exceptions >= 3 || (hasFailures && hasExceptions)) return 'degraded';
    return 'healthy';
}

// ─── Component ──────────────────────────────────────────────────────────────

export function FunctionsHealthTab() {
    const [timespan, setTimespan] = useState('1d');
    const [searchQuery, setSearchQuery] = useState('');

    const { data, isLoading, error, refetch } = useQuery<FunctionHealthRow[]>({
        queryKey: ['admin', 'functions-health', timespan],
        queryFn: async () => {
            const [requestsRes, exceptionsRes] = await Promise.all([
                apiClient.post<AppInsightsResult>('/app-insights-query', {
                    templateId: 'requests-by-endpoint',
                    timespan,
                }),
                apiClient.post<AppInsightsResult>('/app-insights-query', {
                    templateId: 'exception-count-by-function',
                    timespan,
                }),
            ]);

            if (requestsRes.error) {
                const status = requestsRes.error.status;
                const msg = status === 404
                    ? 'Endpoint no encontrado (404). Verifica que la función admin-app-insights-query está desplegada en Azure.'
                    : requestsRes.error.message;
                throw new Error(msg);
            }
            if (exceptionsRes.error) {
                const status = exceptionsRes.error.status;
                const msg = status === 404
                    ? 'Endpoint no encontrado (404). Verifica que la función admin-app-insights-query está desplegada en Azure.'
                    : exceptionsRes.error.message;
                throw new Error(msg);
            }

            const requests = requestsRes.data?.results ?? [];
            const exceptionMap = new Map<string, number>();
            for (const row of exceptionsRes.data?.results ?? []) {
                const op = row.operation_Name ?? row.name;
                const cnt = row.count_ ?? row.count ?? 0;
                if (op) exceptionMap.set(String(op).toLowerCase(), Number(cnt));
            }

            return requests.map((r: Record<string, unknown>) => {
                const name = String(r.name ?? '—');
                const n = name.toLowerCase();
                const withoutMethod = n.replace(/^(get|post|put|delete|patch)\s+/i, '').trim();
                let exceptions = exceptionMap.get(n) ?? exceptionMap.get(withoutMethod) ?? 0;
                if (exceptions === 0) {
                    for (const [op] of exceptionMap) {
                        if (n.includes(op) || withoutMethod.includes(op) || op.includes(withoutMethod)) {
                            exceptions += exceptionMap.get(op) ?? 0;
                            break;
                        }
                    }
                }
                return {
                    name,
                    total: Number(r.total ?? 0),
                    failed: Number(r.failed ?? 0),
                    failure_rate: Number(r.failure_rate ?? 0),
                    avg_duration: Number(r.avg_duration ?? 0),
                    p95_duration: Number(r.p95_duration ?? 0),
                    exceptions,
                };
            });
        },
        staleTime: 60_000,
    });

    const filtered = useMemo(() => {
        if (!data) return [];
        if (!searchQuery.trim()) return data;
        const q = searchQuery.toLowerCase();
        return data.filter(r => r.name.toLowerCase().includes(q));
    }, [data, searchQuery]);

    const summary = useMemo(() => {
        if (!filtered.length) return null;
        const total = filtered.reduce((a, r) => a + r.total, 0);
        const failed = filtered.reduce((a, r) => a + r.failed, 0);
        const withExceptions = filtered.filter(r => r.exceptions > 0).length;
        const critical = filtered.filter(r => getHealthStatus(r) === 'critical').length;
        const degraded = filtered.filter(r => getHealthStatus(r) === 'degraded').length;
        return { total, failed, withExceptions, critical, degraded };
    }, [filtered]);

    return (
        <div className="space-y-4">
            {/* Controls */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-[140px]">
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">
                        Periodo
                    </label>
                    <select
                        value={timespan}
                        onChange={e => setTimespan(e.target.value)}
                        className="w-full px-3 py-2 bg-muted/30 rounded-lg text-sm text-foreground border border-transparent focus:border-primary/30 focus:outline-none cursor-pointer"
                    >
                        {FUNCTIONS_TIMESPANS.map(ts => (
                            <option key={ts.value} value={ts.value}>{ts.label}</option>
                        ))}
                    </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">
                        Buscar función
                    </label>
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Filtrar por nombre..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 bg-muted/30 rounded-lg text-sm border border-transparent focus:border-primary/30 focus:outline-none"
                        />
                    </div>
                </div>
                <button
                    onClick={() => refetch()}
                    disabled={isLoading}
                    className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium transition-all shrink-0 self-end',
                        'bg-primary/10 text-primary hover:bg-primary/20 border border-border',
                        isLoading && 'opacity-50 cursor-not-allowed'
                    )}
                >
                    {isLoading ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    <span className="ml-2">Actualizar</span>
                </button>
            </div>

            {/* Summary cards */}
            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div className="rounded-lg border border-border bg-card/50 px-3 py-2.5">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Requests totales</p>
                        <p className="text-lg font-bold text-foreground">{summary.total.toLocaleString()}</p>
                    </div>
                    <div className="rounded-lg border border-border bg-card/50 px-3 py-2.5">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Fallidos</p>
                        <p className={cn('text-lg font-bold', summary.failed > 0 ? 'text-red-400' : 'text-foreground')}>
                            {summary.failed.toLocaleString()}
                        </p>
                    </div>
                    <div className="rounded-lg border border-border bg-card/50 px-3 py-2.5">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Con excepciones</p>
                        <p className="text-lg font-bold text-foreground">{summary.withExceptions}</p>
                    </div>
                    <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2.5">
                        <p className="text-[10px] text-red-400 uppercase tracking-wider">Críticas</p>
                        <p className="text-lg font-bold text-red-400">{summary.critical}</p>
                    </div>
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
                        <p className="text-[10px] text-amber-400 uppercase tracking-wider">Degradadas</p>
                        <p className="text-lg font-bold text-amber-400">{summary.degraded}</p>
                    </div>
                </div>
            )}

            {/* Error state — mensaje más útil para 404 */}
            {error && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3">
                    <div className="flex items-center gap-2 text-red-400">
                        <XCircle size={16} />
                        <span className="text-sm font-medium">Error al cargar datos</span>
                    </div>
                    <p className="text-xs text-red-300/80 mt-1">{(error as Error).message}</p>
                    <p className="text-[11px] text-muted-foreground mt-2">
                        Si el error es 404: verifica que la función <code className="bg-muted px-1 rounded">admin-app-insights-query</code> esté desplegada y que
                        APP_INSIGHTS_APP_ID y APP_INSIGHTS_API_KEY estén configurados en Azure.
                    </p>
                </div>
            )}

            {/* Table */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border bg-muted/30">
                                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    Función
                                </th>
                                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    Total
                                </th>
                                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    Fallos
                                </th>
                                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    Tasa error %
                                </th>
                                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    Latencia avg
                                </th>
                                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    P95
                                </th>
                                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                                    Excepciones
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td colSpan={8} className="px-4 py-12 text-center">
                                        <Loader2 size={24} className="animate-spin mx-auto text-muted-foreground" />
                                        <p className="text-xs text-muted-foreground mt-2">Cargando datos de Application Insights...</p>
                                    </td>
                                </tr>
                            )}
                            {!isLoading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                                        {data?.length === 0
                                            ? 'No hay funciones con tráfico en el periodo seleccionado.'
                                            : 'No hay resultados que coincidan con el filtro.'}
                                    </td>
                                </tr>
                            )}
                            {!isLoading && filtered.map((row, idx) => {
                                const status = getHealthStatus(row);
                                return (
                                    <tr
                                        key={idx}
                                        className={cn(
                                            'border-b border-border/40 transition-colors',
                                            status === 'critical' && 'bg-red-500/5',
                                            status === 'degraded' && 'bg-amber-500/5',
                                            'hover:bg-muted/5'
                                        )}
                                    >
                                        <td className="px-3 py-2">
                                            {status === 'healthy' && (
                                                <span className="inline-flex items-center gap-1.5 text-emerald-500" title="Saludable">
                                                    <CheckCircle size={14} />
                                                </span>
                                            )}
                                            {status === 'degraded' && (
                                                <span className="inline-flex items-center gap-1.5 text-amber-500" title="Degradada">
                                                    <AlertCircle size={14} />
                                                </span>
                                            )}
                                            {status === 'critical' && (
                                                <span className="inline-flex items-center gap-1.5 text-red-500" title="Crítica">
                                                    <XCircle size={14} />
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 font-medium text-foreground">{row.name}</td>
                                        <td className="px-3 py-2 text-right font-mono">{row.total.toLocaleString()}</td>
                                        <td className={cn('px-3 py-2 text-right font-mono', row.failed > 0 && 'text-red-400')}>
                                            {row.failed}
                                        </td>
                                        <td className={cn('px-3 py-2 text-right font-mono', row.failure_rate >= 5 && 'text-red-400')}>
                                            {row.failure_rate}%
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                                            {row.avg_duration}ms
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                                            {row.p95_duration}ms
                                        </td>
                                        <td className={cn('px-3 py-2 text-right font-mono', row.exceptions > 0 && 'text-amber-400')}>
                                            {row.exceptions}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-[10px] text-muted-foreground">
                Datos de Application Insights. Las funciones sin tráfico en el periodo no aparecen.
            </p>
        </div>
    );
}
